import { VaultAllocation } from '@components/vault/VaultAllocation'
import VaultExposure from '@components/vault/VaultExposure'
import { VaultFormBox } from '@components/vault/VaultFormBox'
import VaultHeader from '@components/vault/VaultHeader'
import { VaultStats } from '@components/vault/VaultStats'
import useSettingsStore from '@store/settingsStore'
import SEO from 'components/Common/SEO'
import { Mode } from 'components/Synthetics/GmSwap/GmSwapBox/GmSwapBox'
import { getSyntheticsDepositMarketKey } from 'config/localStorage'
import { useMarketsInfo } from 'domain/synthetics/markets'
import { useChainId } from 'rfx/lib/chains'
import { getPageTitle } from 'rfx/lib/legacy'
import { useLocalStorageSerializeKey } from 'rfx/lib/localStorage'
import { usePendingTxns } from 'rfx/lib/usePendingTxns'
import useBreakpoints from 'hooks/useBreakpoints'
import { useEffect, useState } from 'react'
import { cn } from 'utils/classnames'
import SharePriceChart from './SharePriceChart'
import commonPoolStore from '@store/commonPoolStore'
import { commonPoolContracts } from 'config/contracts'
import useSWR from 'swr'
import { ethers } from 'ethers'
import CommonPool from 'abis/CommonPool.json'
import { getProvider } from 'rfx/lib/rpc'
import { formatPrice } from 'apis/birdeye/helpers'
import { useCommonPoolAPR } from 'hooks/vault/useCommonPoolAPR'
import dayjs from 'dayjs'
import { formatTradeActionTimestampISO } from 'components/Synthetics/TradeHistoryRow/utils/shared'
import { formatTokenAmount } from 'rfx/lib/numbers'
import { useTokenBalances } from 'domain/synthetics/tokens'
import { getV2Tokens, Token } from 'config/tokens'

enum Operation {
  Deposit = 'Deposit',
  Withdrawal = 'Withdrawal',
}

const VaultHomePage = () => {
  const isPro = useSettingsStore((state) => state.isPro())
  const { below } = useBreakpoints()
  const { chainId } = useChainId()
  const provider = getProvider(undefined, chainId)
  const setSelectedVault = commonPoolStore((store) => store.setSelectedVault)

  const selectedVault = commonPoolStore((store) => store.selectedVault)
  const commonPoolAddress = selectedVault.value
  const contract = new ethers.Contract(
    commonPoolAddress,
    CommonPool.abi,
    provider,
  )
  const { balancesData } = useTokenBalances(chainId)
  const marketToken = Object.values(getV2Tokens(chainId)).find(
    (item) => item.address === commonPoolAddress,
  ) as Token
  // const {
  //   setMarketsInfoDataStore,
  //   setMarketDetailAddress,
  //   marketDetailAddress,
  // } = useCreatePoolStore()
  const { marketsInfoData = {}, tokensData } = useMarketsInfo()
  const markets = Object.values(marketsInfoData)

  const commonPoolAsset = selectedVault.label

  const [, setPendingTxns] = usePendingTxns()

  const [operation, setOperation] = useState<Operation>(Operation.Deposit)
  let [mode, setMode] = useState<Mode>(Mode.Single)

  const { apr, lastRebalance } = useCommonPoolAPR()

  // useEffect(() => {
  //   if (isEmpty(marketsInfoData)) {
  //     return
  //   }
  //   setMarketsInfoDataStore({ marketsInfoData, mutate })
  //   if (!marketDetailAddress) {
  //     setMarketDetailAddress(Object.keys(marketsInfoData)[0])
  //   }
  // }, [marketsInfoData, mutate, setMarketsInfoDataStore])

  const { data } = useSWR(`common-pool-assets-${selectedVault.value}`, {
    fetcher: async () => {
      try {
        const totalAssets = await contract.totalAssets()
        const totalSupply = await contract.totalSupply()
        const symbol = await contract.symbol()
        const name = await contract.name()

        const convertToAssets = await contract.convertToAssets(
          Math.pow(10, 18).toString(), // decimals for share to asset price
        )
        return {
          totalAssets,
          totalSupply,
          convertToAssets,
          symbol,
          name,
        }
      } catch (e) {
        return {
          totalAssets: null,
          totalSupply: null,
          convertToAssets: null,
          symbol: '',
          name: '',
        }
      }
    },
  })

  useEffect(() => {
    if (operation === Operation.Withdrawal) {
      setMode(Mode.Pair)
    }
  }, [operation])

  const [selectedMarketKey, setSelectedMarketKey] = useLocalStorageSerializeKey<
    string | undefined
  >(getSyntheticsDepositMarketKey(chainId), undefined)

  const header = () => {
    return (
      <div className="flex justify-between">
        <div className="justify-start text-2xl font-bold leading-7 text-th-fgd-1">
          Vault Overview
        </div>

        <div className="mr-4 flex items-center gap-4">
          <div className="justify-start text-xs font-medium leading-none text-th-fgd-1">
            SELECT VAULT
          </div>

          {commonPoolContracts[chainId].map((item) => {
            return (
              <div
                className={cn(
                  `inline-flex h-12 w-24 cursor-pointer items-center justify-center gap-2 overflow-hidden rounded px-2 py-2.5 outline outline-1 outline-offset-[-1px]`,
                  item.label !== commonPoolAsset
                    ? 'bg-Neutrals-Celestial-Grey-900 outline-th-input-border'
                    : 'bg-th-active outline-th-active',
                )}
                onClick={() =>
                  setSelectedVault({ label: item.label, value: item.value })
                }
              >
                <div
                  className={cn(
                    `justify-center text-2xl font-medium leading-normal`,
                    item.label !== commonPoolAsset
                      ? 'text-th-active'
                      : 'text-th-fgd-1',
                  )}
                >
                  {item.label}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }
  if (below.lg) {
    return (
      <SEO title={getPageTitle('Vault')}>
        <VaultHeader
          iconSize={32}
          heading={`RFX ${commonPoolAsset} Shared Liquidity Vault`}
        />
        <div className="px-4 pb-4">
          <VaultFormBox
            selectedMarketAddress={selectedMarketKey}
            markets={markets}
            marketsInfoData={marketsInfoData}
            tokensData={tokensData}
            onSelectMarket={setSelectedMarketKey}
            setPendingTxns={setPendingTxns}
            operation={operation}
            mode={mode}
            setMode={setMode}
            setOperation={setOperation}
          />
          <VaultStats />
        </div>
        <div className="pb-4">
          <VaultAllocation />
        </div>
      </SEO>
    )
  }

  return (
    <SEO title={getPageTitle('Vault')}>
      <div className="mx-6 flex h-full w-full justify-between">
        <div className="my-6 flex w-full flex-col overflow-hidden">
          {header()}

          {/* section 2 */}
          <div className="mt-6 flex w-full">
            <div className="flex-1 text-base font-medium leading-tight text-th-fgd-1">
              {`Deposit ${commonPoolAsset} to mint SLV Tokens and earn fees from leverage trading. The SLV automatically rebalances liquidity to the best performing markets with 0 impermanent loss.`}
            </div>
            <div className="flex flex-1 items-center justify-start">
              <VaultOverviewSection
                label={'APR (1D)'}
                value={(apr || 0)?.toFixed(4) + '%'}
              />
              <div className="mx-10 h-10 w-0 origin-top-left rotate-0 outline outline-1 outline-offset-[-0.50px] outline-th-active"></div>
              <VaultOverviewSection
                label={'Price'}
                value={formatPrice(
                  data?.convertToAssets * Math.pow(10, -18),
                  4,
                )}
              />

              <div className="mx-10 h-10 w-0 origin-top-left rotate-0 outline outline-1 outline-offset-[-0.50px] outline-th-active"></div>
              <VaultOverviewSection
                label={'wallet'}
                value={
                  balancesData
                    ? formatTokenAmount(
                        balancesData?.[marketToken?.address] || 0,
                        marketToken?.decimals,
                        '',
                        {
                          useCommas: true,
                          displayDecimals: 4,
                        },
                      ) || ''
                    : ''
                }
              />
            </div>
          </div>

          {/* section 3 */}
          <div className="mt-16 flex w-full flex-col gap-4">
            <div className="justify-start text-2xl font-bold leading-7 text-th-fgd-1">
              Vault Info
            </div>

            <div className="flex-1 text-base font-medium leading-tight text-th-fgd-1">
              {`Deposit ${commonPoolAsset} anytime to receive SLV tokens based on current vault
              TVL. SLV price increases as the vault earns fees from RFX markets.`}
            </div>
            <div className="flex flex-1 items-center justify-start">
              <VaultInfoSection
                label={`Total ${commonPoolAsset} Amount of assets`}
                value={formatPrice(
                  Number(data?.totalAssets) * Math.pow(10, -18),
                  4,
                )}
                subValue={commonPoolAsset}
              />
              <div className="mx-10 h-10 w-0 origin-top-left rotate-0 outline outline-1 outline-offset-[-0.50px] outline-th-active"></div>
              <VaultInfoSection
                label={`Total Supply of SLV ${commonPoolAsset} tokens`}
                value={formatPrice(
                  Number(data?.totalSupply * Math.pow(10, -18)),
                  4,
                )}
                subValue={commonPoolAsset}
              />
              <div className="mx-10 h-10 w-0 origin-top-left rotate-0 outline outline-1 outline-offset-[-0.50px] outline-th-active"></div>
              <VaultInfoSection
                label="Last Rebalance"
                value={dayjs(
                  formatTradeActionTimestampISO(lastRebalance),
                ).format('DD MMM YYYY')}
                subValue={dayjs(
                  formatTradeActionTimestampISO(lastRebalance),
                ).format(' hh:mm a')}
              />
            </div>
          </div>

          <div className="mt-16 h-full overflow-auto">
            <div className="flex h-72 w-full gap-4">
              <div className="flex-1">
                <div className="justify-start text-2xl font-bold leading-7 text-th-fgd-1">
                  Vault Exposure
                </div>

                <VaultAllocation />
              </div>
              <div className="flex-1">
                <SharePriceChart />
              </div>
            </div>
          </div>
        </div>

        <div
          className={cn(
            'relative mr-4 flex shrink-0 flex-col overflow-scroll border-l border-th-input-border px-4',
            isPro ? 'w-[316px]' : 'w-[396px]',
          )}
        >
          <VaultFormBox
            selectedMarketAddress={selectedMarketKey}
            markets={markets}
            marketsInfoData={marketsInfoData}
            tokensData={tokensData}
            onSelectMarket={setSelectedMarketKey}
            setPendingTxns={setPendingTxns}
            operation={operation}
            mode={mode}
            setMode={setMode}
            setOperation={setOperation}
          />
          <VaultStats />
          <VaultExposure />
        </div>
      </div>
    </SEO>
  )
}

export default VaultHomePage

const VaultOverviewSection = ({
  label,
  value,
}: {
  label: string
  value: string
}) => {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 align-middle">
      <div className="justify-start text-xs font-medium capitalize leading-3 text-[#6C757D]">
        {label}
      </div>
      <div className="text-Greyscale-Text-Primary justify-start text-2xl font-medium leading-normal">
        {value}
      </div>
    </div>
  )
}

const VaultInfoSection = ({
  label,
  value,
  subValue,
}: {
  label: string
  value: string
  subValue: string
}) => {
  return (
    <div className="flex flex-1 flex-row items-center justify-center gap-4 align-middle">
      <div className="justify-start text-sm font-medium capitalize leading-tight text-[#6C757D]">
        {label}
      </div>
      <div className="flex flex-col items-end justify-end gap-1">
        <div className="text-Greyscale-Text-Primary justify-start text-2xl font-medium leading-normal">
          {value}
        </div>
        <div className="text-Greyscale-Text-Primary justify-start text-2xl font-medium leading-normal">
          {subValue}
        </div>
      </div>
    </div>
  )
}
